import { type SignInInput, signInSchema } from '@amore/shared'
import { zodResolver } from '@hookform/resolvers/zod'
import type { QueryClient } from '@tanstack/react-query'
import { useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link, redirect, useNavigate } from '@tanstack/react-router'
import { Heart, Sparkles } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { GoogleAuthButton } from '@/features/auth/google-auth-button'
import { LoginPreview } from '@/features/auth/login-preview'
import { DevLoginButton } from '@/features/dev/dev-login-button'
import { signIn } from '@/lib/auth'
import { ensureSession, refreshSession } from '@/lib/session'
import { ensureFirstWorkspaceSlug } from '@/lib/workspace'

const searchSchema = z.object({
  redirect: z.string().optional(),
  email: z.string().optional(),
})

function shouldUseDefaultDestination(redirectTo?: string) {
  return !redirectTo || redirectTo === '/workspaces'
}

export const Route = createFileRoute('/login')({
  validateSearch: (search) => searchSchema.parse(search),
  beforeLoad: async ({ context, search }) => {
    const session = await ensureSession(context.queryClient)

    if (session) {
      if (!shouldUseDefaultDestination(search.redirect)) {
        throw redirect({ to: search.redirect })
      }

      throw redirect(await defaultRedirect(context.queryClient))
    }
  },
  component: LoginPage,
})

async function defaultRedirect(queryClient: QueryClient) {
  const workspaceSlug = await ensureFirstWorkspaceSlug(queryClient)

  return workspaceSlug
    ? ({ to: '/w/$workspaceSlug', params: { workspaceSlug } } as const)
    : ({ to: '/onboarding' } as const)
}

function LoginPage() {
  const { redirect: redirectTo, email } = Route.useSearch()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [formError, setFormError] = useState<string | null>(null)
  const invitedEmail = email?.trim() ?? ''
  const authSearch = {
    ...(redirectTo ? { redirect: redirectTo } : {}),
    ...(invitedEmail ? { email: invitedEmail } : {}),
  }

  // The same schema the API enforces — one definition, two consumers.
  const form = useForm<SignInInput>({
    resolver: zodResolver(signInSchema),
    defaultValues: { email: invitedEmail, password: '' },
  })

  const onSubmit = form.handleSubmit(async (values) => {
    setFormError(null)

    const { error } = await signIn.email(values)

    if (error) {
      setFormError(error.message ?? 'Nepodařilo se vás přihlásit.')
      return
    }

    await refreshSession(queryClient)

    if (shouldUseDefaultDestination(redirectTo)) {
      const workspaceSlug = await ensureFirstWorkspaceSlug(queryClient)

      if (workspaceSlug) {
        await navigate({ to: '/w/$workspaceSlug', params: { workspaceSlug } })
        return
      }

      await navigate({ to: '/onboarding' })
      return
    }

    await navigate({ to: redirectTo })
  })

  return (
    <main className="h-dvh overflow-hidden bg-[linear-gradient(135deg,#fbf8f5_0%,#f4ece6_48%,#eef4ef_100%)] text-foreground">
      <div className="mx-auto grid h-full w-full max-w-6xl items-center gap-8 px-5 py-4 lg:grid-cols-[1.08fr_0.92fr] lg:px-10">
        <LoginPreview />

        <section
          className="w-full max-w-md justify-self-center lg:justify-self-end"
          aria-labelledby="login-heading"
        >
          <div className="page-stagger-root relative rounded-[8px] border border-white/70 bg-card/95 p-4 shadow-[0_20px_64px_rgba(43,35,32,0.13)] backdrop-blur sm:p-5">
            {import.meta.env.DEV && <DevLoginButton />}

            <div className="flex items-center gap-3 pr-12">
              <div className="flex items-center gap-3">
                <div className="grid size-9 place-items-center rounded-[8px] bg-primary text-primary-foreground lg:hidden">
                  <Heart className="size-5" aria-hidden="true" />
                </div>
                <div>
                  <p className="font-heading text-xl text-foreground">Amoré</p>
                </div>
              </div>
            </div>

            {invitedEmail && (
              <div className="mt-4 rounded-[8px] border border-[#c9ddd5] bg-[#eef4ef] px-3 py-2 text-sm text-[#3f6d52]">
                Pozvánka je připravená pro <span className="font-medium">{invitedEmail}</span>.
              </div>
            )}

            <div className="mt-5">
              <p className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                <Sparkles className="size-3.5 text-primary" aria-hidden="true" />
                Vítejte zpátky
              </p>
              <h1
                id="login-heading"
                className="mt-1.5 font-heading text-3xl font-normal leading-tight sm:text-4xl"
              >
                Přihlášení do Amoré
              </h1>
              <p className="mt-2 text-sm leading-5 text-muted-foreground sm:leading-6">
                Pokračujte v plánování svatebního dne bez ztracených tabulek a poznámek.
              </p>
            </div>

            <div className="mt-5">
              <GoogleAuthButton
                redirectTo={redirectTo}
                email={invitedEmail}
                mode="signin"
                onError={setFormError}
              />
            </div>

            <div className="my-4 flex items-center gap-3 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              <div className="h-px flex-1 bg-border" />
              <span>nebo</span>
              <div className="h-px flex-1 bg-border" />
            </div>

            <form onSubmit={onSubmit} className="space-y-3" noValidate>
              <div className="space-y-2">
                <label htmlFor="email" className="text-sm font-medium">
                  E-mail
                </label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  readOnly={Boolean(invitedEmail)}
                  placeholder="vy@example.com"
                  aria-invalid={Boolean(form.formState.errors.email)}
                  {...form.register('email')}
                />
                {form.formState.errors.email && (
                  <p className="text-sm text-destructive">{form.formState.errors.email.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <label htmlFor="password" className="text-sm font-medium">
                  Heslo
                </label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  placeholder="*****"
                  aria-invalid={Boolean(form.formState.errors.password)}
                  {...form.register('password')}
                />
                {form.formState.errors.password && (
                  <p className="text-sm text-destructive">
                    {form.formState.errors.password.message}
                  </p>
                )}
              </div>

              {formError && (
                <p
                  role="alert"
                  className="rounded-[8px] border border-destructive/20 bg-destructive/10 px-3 py-2 text-sm text-destructive"
                >
                  {formError}
                </p>
              )}

              <Button type="submit" className="h-10 w-full" disabled={form.formState.isSubmitting}>
                {form.formState.isSubmitting ? 'Přihlašuji...' : 'Přihlásit se'}
              </Button>
            </form>

            <p className="mt-4 text-center text-sm text-muted-foreground">
              Ještě nemáte účet?{' '}
              <Link
                to="/signup"
                search={authSearch}
                className="font-medium text-foreground underline underline-offset-4"
              >
                Vytvořit účet
              </Link>
            </p>
          </div>
        </section>
      </div>
    </main>
  )
}
